import { FINANCE_CONFIG } from '@financego/core';
import { SlidersHorizontal, X } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';

import { useDialogFocusTrap } from '../../hooks/useDialogFocusTrap';
import { useI18n } from '../../i18n/useI18n';
import type { TranslationKey } from '../../i18n/messages';
import type { PlanFilters, PlanMovementFilter } from './plan-filter.types';

const movementOptions: readonly { id: PlanMovementFilter; label: TranslationKey }[] = [
  { id: 'all', label: 'dashboard.filterAll' },
  { id: 'recurring', label: 'dashboard.filterRecurring' },
  { id: 'one-time', label: 'dashboard.filterOneTime' },
  { id: 'transfers', label: 'dashboard.filterTransfers' },
];

export function PlanFilterSheet({
  filters,
  onApply,
  onClose,
}: {
  readonly filters: PlanFilters;
  readonly onApply: (filters: PlanFilters) => void;
  readonly onClose: () => void;
}) {
  const { locale, t } = useI18n();
  const [draft, setDraft] = useState(filters);
  const sheetRef = useDialogFocusTrap(onClose);
  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => ({
        id: index + 1,
        label: new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' }).format(
          new Date(Date.UTC(2026, index, 1)),
        ),
      })),
    [locale],
  );
  const years = Array.from(
    { length: FINANCE_CONFIG.projection.maxYear - FINANCE_CONFIG.projection.minYear + 1 },
    (_, index) => FINANCE_CONFIG.projection.minYear + index,
  );

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onApply(draft);
    onClose();
  };

  return (
    <div
      className="bottom-sheet-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
    >
      <section
        aria-labelledby="plan-filter-title"
        aria-modal="true"
        className="filter-bottom-sheet"
        data-filter-context="plan"
        ref={sheetRef}
        role="dialog"
      >
        <span className="bottom-sheet-handle" aria-hidden="true" />
        <header className="filter-sheet-heading">
          <div>
            <p className="section-kicker">{t('navigation.plan')}</p>
            <h2 id="plan-filter-title">{t('plan.filterTitle')}</h2>
            <p>{t('plan.filterDescription')}</p>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label={t('common.close')}
            onClick={onClose}
          >
            <X aria-hidden="true" />
          </button>
        </header>

        <form className="plan-filter-form" onSubmit={submit}>
          <div className="filter-period-grid">
            <label>
              {t('plan.month')}
              <select
                autoFocus
                value={draft.month}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, month: Number(event.target.value) }))
                }
              >
                {months.map((month) => (
                  <option key={month.id} value={month.id}>
                    {month.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t('plan.year')}
              <select
                value={draft.year}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, year: Number(event.target.value) }))
                }
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label>
            {t('plan.movementKind')}
            <select
              value={draft.movement}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  movement: event.target.value as PlanMovementFilter,
                }))
              }
            >
              {movementOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {t(option.label)}
                </option>
              ))}
            </select>
          </label>

          <label className="occurrence-filter-switch">
            <span className="filter-switch-icon">
              <SlidersHorizontal aria-hidden="true" />
            </span>
            <span>
              <strong>{t('plan.showOccurrences')}</strong>
              <small>{t('plan.showOccurrencesHelp')}</small>
            </span>
            <input
              checked={draft.showOccurrences}
              role="switch"
              type="checkbox"
              onChange={(event) =>
                setDraft((current) => ({ ...current, showOccurrences: event.target.checked }))
              }
            />
          </label>

          <div className="filter-sheet-actions">
            <button
              className="button button-ghost"
              type="button"
              onClick={() =>
                setDraft({
                  month: Number(filters.month),
                  movement: 'all',
                  showOccurrences: false,
                  year: Number(filters.year),
                })
              }
            >
              {t('plan.resetFilters')}
            </button>
            <button className="button button-primary" type="submit">
              {t('plan.applyFilters')}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
